import { omit } from "lodash";
import type { DateInputState } from "@/components-v2/DateInput";
import { DCEvent } from "@/events/eventsDomain";
import { Language } from "@/i18n/languages";
import { TMessage, TMessageParam } from "@/i18n/tt";
import { TMessageKey } from "@/i18n/types.gen";
import { isCountryCode } from "@/utils/countries";
import { isValidDate, parseDateInput, toLocalDateISOString } from "@/utils/Dates";
import { filterInputString, formatInputString, parseInputString } from "@/utils/numberInput";
import { multiValue, QueryParamValue } from "@/utils/queryParams";

export type ComparisonOperator = "gt" | "lt" | "eq" | "contains";

export type LogicalOperator = "and" | "or";

export type FilterCondition<Id extends string> = {
  field: Id;
  operator: ComparisonOperator;
  value: string;
};

export type FilterGroup<Id extends string> = {
  logicalOperator: LogicalOperator;
  conditions: FilterCondition<Id>[];
};

export type FilterOption = { value: string; label: TMessage };

export type FilterField<Id extends string> = { id: Id; label: TMessage | TMessageParam } & (
  | { type: "text" }
  | { type: "number"; suffix?: string }
  | { type: "options"; options: FilterOption[] }
  | { type: "date" }
  | { type: "country" }
);

export type FilterDialogCondition<Id extends string> = FilterCondition<Id> & {
  input?: string;
  date?: DateInputState;
};

export type FilterDialogState<Id extends string> = {
  fields: FilterField<Id>[];
  logicalOperator: LogicalOperator;
  conditions: FilterDialogCondition<Id>[];
};

export type FilterDialogUpdate<Id extends string> = (
  f: (state: FilterDialogState<Id>) => FilterDialogState<Id>,
) => DCEvent;

export type FilterDialogRow<Id extends string> = {
  index: number;
  condition: FilterDialogCondition<Id>;
  field: FilterField<Id>;
};

const COMPARISON_OPERATORS: Record<FilterField<string>["type"], ComparisonOperator[]> = {
  text: ["contains"],
  number: ["gt", "lt", "eq"],
  options: ["eq"],
  date: ["gt", "lt", "eq"],
  country: ["eq"],
};

const COMPARISON_OPERATOR_LABELS: Record<ComparisonOperator, TMessageKey> = {
  gt: "filter-dialog.comparison-operator.gt",
  lt: "filter-dialog.comparison-operator.lt",
  eq: "filter-dialog.comparison-operator.eq",
  contains: "filter-dialog.comparison-operator.contains",
};

const DATE_OPERATOR_LABELS: Partial<Record<ComparisonOperator, TMessageKey>> = {
  gt: "filter-dialog.comparison-operator.date.gt",
  lt: "filter-dialog.comparison-operator.date.lt",
  eq: "filter-dialog.comparison-operator.date.eq",
};

const NUMBER_INPUT = { useGrouping: true, isNegative: true };

export const fieldOperators = <Id extends string>({
  type,
}: FilterField<Id>): ComparisonOperator[] => COMPARISON_OPERATORS[type];

export const operatorLabel = <Id extends string>(
  { type }: FilterField<Id>,
  operator: ComparisonOperator,
): TMessage => ({
  key:
    (type === "date" ? DATE_OPERATOR_LABELS[operator] : undefined) ??
    COMPARISON_OPERATOR_LABELS[operator],
});

const newCondition = <Id extends string>(field: FilterField<Id>): FilterDialogCondition<Id> => ({
  field: field.id,
  operator: fieldOperators(field)[0],
  value: field.type === "options" ? (field.options[0]?.value ?? "") : "",
});

const findField = <Id extends string>(
  { fields }: Pick<FilterDialogState<Id>, "fields">,
  id: Id,
): FilterField<Id> | undefined => fields.find((field) => field.id === id);

const updateCondition = <Id extends string>(
  state: FilterDialogState<Id>,
  index: number,
  f: (condition: FilterDialogCondition<Id>) => FilterDialogCondition<Id>,
): FilterDialogState<Id> => ({
  ...state,
  conditions: state.conditions.map((condition, i) => (i === index ? f(condition) : condition)),
});

export const initFilterDialogState = <Id extends string>(
  fields: FilterField<Id>[],
  group: FilterGroup<Id> | undefined,
): FilterDialogState<Id> => {
  const conditions = (group?.conditions ?? []).filter(({ field }) =>
    fields.some(({ id }) => id === field),
  );
  return {
    fields,
    logicalOperator: group?.logicalOperator ?? "and",
    conditions: conditions.length > 0 ? conditions : [newCondition(fields[0])],
  };
};

export const filterDialogRows = <Id extends string>(
  state: FilterDialogState<Id>,
): FilterDialogRow<Id>[] =>
  state.conditions.flatMap((condition, index) => {
    const field = findField(state, condition.field);
    return field ? [{ index, condition, field }] : [];
  });

export const conditionInput = <Id extends string>(
  { field, condition: { value, input } }: Pick<FilterDialogRow<Id>, "field" | "condition">,
  lang: Language,
): string =>
  input ??
  (field.type === "number" && value
    ? formatInputString(Number(value), lang, { useGrouping: true })
    : value);

export const conditionDate = <Id extends string>({
  condition: { value, date },
}: Pick<FilterDialogRow<Id>, "condition">): DateInputState => date ?? parseDateInput(value);

export const setLogicalOperator = <Id extends string>(
  state: FilterDialogState<Id>,
  logicalOperator: LogicalOperator,
): FilterDialogState<Id> => ({ ...state, logicalOperator });

export const setConditionField = <Id extends string>(
  state: FilterDialogState<Id>,
  index: number,
  field: FilterField<Id>,
): FilterDialogState<Id> => updateCondition(state, index, () => newCondition(field));

export const setConditionOperator = <Id extends string>(
  state: FilterDialogState<Id>,
  index: number,
  operator: ComparisonOperator,
): FilterDialogState<Id> =>
  updateCondition(state, index, (condition) => ({ ...condition, operator }));

export const setConditionInput = <Id extends string>(
  state: FilterDialogState<Id>,
  index: number,
  input: string,
  lang: Language,
): FilterDialogState<Id> =>
  updateCondition(state, index, (condition) => {
    if (findField(state, condition.field)?.type !== "number") {
      return { ...condition, value: input };
    }
    const filtered = filterInputString(input, lang, NUMBER_INPUT);
    return {
      ...condition,
      value: parseInputString(filtered, lang)?.toString() ?? "",
      input: filtered,
    };
  });

export const setConditionDate = <Id extends string>(
  state: FilterDialogState<Id>,
  index: number,
  date: DateInputState,
): FilterDialogState<Id> =>
  updateCondition(state, index, (condition) => ({
    ...condition,
    value: date.parsed ? toLocalDateISOString(date.parsed) : "",
    date,
  }));

export const commitConditionInput = <Id extends string>(
  state: FilterDialogState<Id>,
  index: number,
): FilterDialogState<Id> => updateCondition(state, index, (condition) => omit(condition, "input"));

export const addCondition = <Id extends string>(
  state: FilterDialogState<Id>,
): FilterDialogState<Id> => ({
  ...state,
  conditions: [...state.conditions, newCondition(state.fields[0])],
});

export const removeCondition = <Id extends string>(
  state: FilterDialogState<Id>,
  index: number,
): FilterDialogState<Id> => ({
  ...state,
  conditions: state.conditions.filter((_, i) => i !== index),
});

export const resetCondition = <Id extends string>(
  state: FilterDialogState<Id>,
  index: number,
): FilterDialogState<Id> => updateCondition(state, index, () => newCondition(state.fields[0]));

export const toFilterGroup = <Id extends string>({
  logicalOperator,
  conditions,
}: FilterDialogState<Id>): FilterGroup<Id> | undefined => {
  const filled = conditions.flatMap(({ field, operator, value }) =>
    value.trim() ? [{ field, operator, value: value.trim() }] : [],
  );
  return filled.length > 0 ? { logicalOperator, conditions: filled } : undefined;
};

const OPERATOR_SYMBOLS: Record<ComparisonOperator, string> = {
  gt: ">",
  lt: "<",
  eq: "=",
  contains: "",
};

const valueLabel = <Id extends string>(
  field: FilterField<Id>,
  value: string,
): TMessage | TMessageParam | string => {
  switch (field.type) {
    case "text":
      return value;
    case "number":
      return `${value}${field.suffix ?? ""}`;
    case "options":
      return field.options.find((option) => option.value === value)?.label ?? value;
    case "date":
      return isValidDate(value) ? { type: "date", value } : value;
    case "country":
      return isCountryCode(value) ? { type: "country", code: value } : value;
  }
};

const conditionTag = <Id extends string>(
  { field: id, operator, value }: FilterCondition<Id>,
  fields: FilterField<Id>[],
): TMessageParam => {
  const field = findField({ fields }, id);
  return {
    type: "text",
    value: "{{column}} {{operator}} {{value}}",
    params: {
      column: field?.label ?? { translated: id },
      operator: OPERATOR_SYMBOLS[operator],
      value: field ? valueLabel(field, value) : value,
    },
  };
};

export const filterTag = <Id extends string>(
  group: FilterGroup<Id> | undefined,
  fields: FilterField<Id>[],
): TMessage | TMessageParam | undefined => {
  const conditions = group?.conditions ?? [];
  if (conditions.length === 0) return undefined;
  if (conditions.length === 1) return conditionTag(conditions[0], fields);
  return { translated: String(conditions.length) };
};

const FILTER_PATTERN = /^(.+?)__(gt|lt|eq|contains)__(.*)$/;

export const encodeFilters = <Id extends string>(
  group: FilterGroup<Id> | undefined,
): { filterOp?: string; filters?: string[] } =>
  group && group.conditions.length > 0
    ? {
        filterOp: group.logicalOperator,
        filters: group.conditions.map(
          ({ field, operator, value }) => `${field}__${operator}__${value}`,
        ),
      }
    : {};

const decodeCondition = <Id extends string>(
  isFieldId: (id: string) => id is Id,
  filter: string,
): FilterCondition<Id>[] => {
  const [, field, operator, value] = FILTER_PATTERN.exec(filter) ?? [];
  return field !== undefined && isFieldId(field)
    ? [{ field, operator: operator as ComparisonOperator, value }]
    : [];
};

export const decodeFilters = <Id extends string>(
  isFieldId: (id: string) => id is Id,
  filterOp: string | undefined,
  filters: QueryParamValue,
): FilterGroup<Id> | undefined => {
  const conditions = (multiValue(filters) ?? []).flatMap((filter) =>
    decodeCondition(isFieldId, filter),
  );
  return conditions.length > 0
    ? { logicalOperator: filterOp === "or" ? "or" : "and", conditions }
    : undefined;
};
